import { Box, Button, CircularProgress, Grid, Paper, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Modal from "../App/Components/Modal/Modal";
import Breadcrumb from "../App/Components/Navigation/Subcomponents/Breadcrumbs/Breadcrumbs";
import { PageContainer } from "../App/Components/PageContainer/PageContainer";
import { PageTitle } from "../App/Components/PageTitle/PageTitle";
import SortableTable from "../App/Components/Table/SortableTable";
import { useAllImportsForClient, useImportStatus } from "../App/Queries/ImportQueries";
import { useImportTabStore } from "../App/State/Stores/ImportTabStore";
import { useUserProfileStore } from "../App/State/Stores/UserProfileStore";
import { IImportStatus } from "../App/Typings/Imports";
import { ITableColumn } from "../App/Typings/TableColumn";
import { ImportsModalFooter } from "./Components/ImportsModalFooter";
import { ImportStatusDetails } from "./Components/ImportStatusDetails";
import ImportLogTabs from "./ImportLogTabs";

export const tabKeyToLabel = {
  imports: "Imports",
  conflicts: "Conflicts",
  errors: "Errors",
} as Record<string, string>;

const columns: ITableColumn[] = [
  {
    label: "Created Date",
    order: 0,
    propName: "createdDate",
  },
  {
    label: "Status",
    order: 1,
    propName: "status",
  },
  {
    label: "Username",
    order: 2,
    propName: "username",
  },
  {
    label: "File Name",
    order: 3,
    propName: "fileName",
  },
  {
    label: "Group",
    order: 4,
    propName: "groupId",
  },
  {
    label: "Total Rows",
    order: 5,
    propName: "totalRows",
  },
  {
    label: "Imports",
    order: 6,
    propName: "totalSuccesses",
  },
  {
    label: "Conflicts",
    order: 7,
    propName: "totalConflicts",
  },
  {
    label: "Errors",
    order: 8,
    propName: "errorCount",
  },
  {
    label: "Completed Date",
    order: 9,
    propName: "completedDate",
  },
  {
    label: "Logs",
    order: 10,
    propName: "errorMessages",
  },
];

export default function Imports() {
  const [selectedStatus, setSelectedStatus] = useState<IImportStatus>();
  const { getUserAdjustedDate } = useUserProfileStore();
  const [search, setSearch] = useSearchParams();
  const { id: groupId } = useParams();
  const { data: imports, isLoading } = useAllImportsForClient(groupId);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const resetImportStatusTabs = useImportTabStore((state) => state.reset);
  const resetPagination = useImportTabStore((state) => state.resetPagination);

  const visibleColumns = groupId ? columns.filter((c) => c.propName !== "groupId") : columns;

  const handleViewLogs = (status: IImportStatus) => {
    setSelectedStatus(status);
    setModalOpen(true);
    setSearch({ importId: status.id });
  };

  const handleCloseDetails = () => {
    setModalOpen(false);
    setTimeout(() => {
      setSearch({});
      setSelectedStatus(undefined);
      resetImportStatusTabs();
      resetPagination();
    }, 500);
  };

  useEffect(() => {
    const importId = search.get("importId");
    if (importId && imports) {
      setSelectedStatus(imports.find((i) => i.id === importId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imports]);

  useEffect(() => {
    const importId = search.get("importId");
    if (selectedStatus && importId && selectedStatus.id === importId) {
      setModalOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatus]);

  return (
    <PageContainer>
      <Grid container>
        <Grid item xs={12}>
          <Breadcrumb pageTitle="Candidate Imports" />
        </Grid>
      </Grid>
      <Paper sx={{ mt: { xs: 1, md: 2 }, p: { xs: 2, lg: 4 }, flex: 1 }}>
        <Grid container>
          <Grid item xs={12} sx={{ marginBottom: "10px" }}>
            <PageTitle sx={{ lineHeight: 1 }}>Candidate Imports</PageTitle>
          </Grid>
        </Grid>
        <SortableTable
          columns={visibleColumns}
          name={groupId ? `candidate-imports-${groupId}` : "candidate-imports"}
          tableData={imports}
          emptyMessage="No imports"
          exportEnabled={false}
          defaultOrderBy="createDate"
          loading={isLoading && !imports}
          pagination={{
            rowsPerPage: [10, 25, 50, 100],
            enabled: true,
          }}
          columnEditEnabled={false}
          renderCustomCell={(row, column) => {
            if (column.propName === "errorMessages") {
              const hasConflicts = (row as IImportStatus).pendingConflicts > 0;
              return (
                <Button
                  title={hasConflicts ? `Clear ${row.pendingConflicts} Conflicts` : "View Import Details"}
                  onClick={() => handleViewLogs(row as IImportStatus)}
                >
                  View Logs
                </Button>
              );
            }

            if (column.propName.toLowerCase().indexOf("date") >= 0) {
              const date = getUserAdjustedDate(row[column.propName]);

              return date.isValid() ? date.format("LLL") : "";
            }

            if (column.propName === "groupId") {
              return row[column.propName] !== "00000000-0000-0000-0000-000000000000" ? (
                <Button component={Link} to={`/candidategroups/${row[column.propName]}`} target="_blank">
                  View Group
                </Button>
              ) : (
                <></>
              );
            }

            return row[column.propName];
          }}
        />
      </Paper>
      <Modal
        content={
          search.get("importId") && selectedStatus ? (
            <ImportStatus statusId={search.get("importId") ?? ""} />
          ) : (
            <></>
          )
        }
        isOpen={modalOpen}
        dataTestId="import-logs"
        footerContent={<ImportsModalFooter statusId={selectedStatus?.id ?? ""} />}
        footerDivider={true}
        handleCloseModal={handleCloseDetails}
        title="Import Logs"
        size="lg"
        sx={{
          "& .MuiDialogActions-root": {
            display: "block",
          },
        }}
      />
    </PageContainer>
  );
}

function ImportStatus({ statusId }: { statusId: string }) {
  const { data: importStatus, isLoading } = useImportStatus(statusId);

  return isLoading || !importStatus ? (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
      <CircularProgress />
    </Box>
  ) : (
    <>
      <ImportStatusDetails status={importStatus} isFetching={false} />
      <DisplayImportError errorMessages={importStatus.errorMessages} />
      <ImportLogTabs importStatus={importStatus} />
    </>
  );
}

function DisplayImportError({ errorMessages }: { errorMessages?: string }) {
  if (!errorMessages) {
    return null;
  }
  let errorMessagesJson = {} as { errors: string[]; basicMessage: string };
  const defaultError = (
    <Typography component="div" variant="caption" color="error">
      {errorMessages}
    </Typography>
  );
  if (errorMessages.startsWith("{")) {
    try {
      errorMessagesJson = JSON.parse(errorMessages);
    } catch (e) {
      console.error("Error parsing import error messages", e);
      return defaultError;
    }
    return (
      <Typography component="div" variant="caption" color="error">
        Import had {errorMessagesJson.errors.length} errors
        <br />
        {errorMessagesJson.basicMessage}
      </Typography>
    );
  } else {
    return defaultError;
  }
}

export const getImportStatusTypeDisplay = (currentStatus: string) => {
  // Regex a string with spaces between capitalized words, e.g. "Job Alerts" instead of "JobAlerts"
  return currentStatus.replace(/([A-Z])/g, " $1").trim();
};

export const getChangeSourceDisplay = (changeSource: string) => {
  switch (changeSource) {
    case "LeadForm":
      return "Lead Form";
    case "EventRsvp":
      return "Event RSVP";
    case "ProfileEdit":
      return "Profile Edit";
    case "CsvUpdate":
      return "CSV Update";
    case "Manual":
      return "Manually added";
    case "StatusChange":
      return "Status Change";
    default:
      return changeSource;
  }
};
